import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatOption } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { s3UrlPipe } from '@fuse/pipes/s3Url.pipe';
import { FuseConfig, FuseConfigService, Scheme } from '@fuse/services/config';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoPipe } from '@jsverse/transloco';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { CompanyService } from 'app/services/company.service';
import * as global from 'global';
import { map, Observable, startWith, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    styleUrls: ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        FuseLoadingBarComponent,
        CommonModule,
        FuseVerticalNavigationComponent,
        // NotificationsComponent,
        UserComponent,
        MatIconModule,
        MatButtonModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        s3UrlPipe,
        MatFormField,
        MatInput,
        MatAutocompleteModule,
        MatOption,
        TranslocoPipe,
        FormsModule,
        ReactiveFormsModule,
        // SearchComponent,
        // ShortcutsComponent,
        // MessagesComponent,
        RouterOutlet,
        // QuickChatComponent,
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    private _companyService = inject(CompanyService);
    isScreenSmall: boolean;
    navigation: Navigation;
    scheme: 'dark' | 'light';
    user: User;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    config: FuseConfig;
    adminVersion: string;
    filteredClients: Observable<any[]>;
    clients: any[] = [];

    searchClientsCtrl = new FormControl();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseConfigService: FuseConfigService
    ) {}

    /**
     * Logo del sidebar según marca (por dominio). Detección inline (NO BrandService)
     * porque este layout se sincroniza a mb_client, donde app/brand no existe.
     */
    get logoSrc(): string {
        let isMuve = false;
        try {
            const h = (typeof window !== 'undefined' ? window.location.hostname : '').toLowerCase();
            isMuve = /(^|\.)muve\.(cl|app|dev)$/.test(h) || (typeof localStorage !== 'undefined' && localStorage.getItem('brand') === 'muve');
        } catch {
            isMuve = false;
        }
        return isMuve ? 'assets/images/logo/muve/muve-wordmark-light.svg' : 'images/logo/logo-white.svg';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
        });

        this._navigationService.version$.pipe(takeUntil(this._unsubscribeAll)).subscribe((version: string) => {
            this.adminVersion = version;
        });

        // Subscribe to the user service
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
            this.user = user;
            this.searchClientsCtrl.setValue(this.user['client']);
        });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            // Check if the screen is small
            this.isScreenSmall = !matchingAliases.includes('md');
        });
        this._fuseConfigService.config$.pipe(takeUntil(this._unsubscribeAll)).subscribe((config: FuseConfig) => {
            // Store the config
            this.config = config;
        });

        const user = JSON.parse(window.localStorage.getItem(global.LOCAL_USER_KEY) ?? '{}');

        if (user?.role !== 'app') {
            this._companyService
                .getClients()
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe({
                    next: (response) => {
                        console.log('✅ Clients cargados:', response);
                        this.clients = Array.isArray(response.clients) ? response.clients : [];
                        this.filteredClients = this.searchClientsCtrl.valueChanges.pipe(
                            startWith(''),
                            map((value) => this._filter(value, this.clients))
                        );
                    },
                });
        }
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void {
        this._fuseConfigService.config = { scheme };
        localStorage.setItem('scheme', scheme);
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    clearSearchInput(): void {
        this.searchClientsCtrl.setValue('');
    }

    getDisplayFn() {
        return (val) => (val && val.name ? val.name : val);
    }

    setClient(val) {
        this._userService.setClient(val.uuid).subscribe(() => location.assign(''));
    }

    /**
     * Filtra en memoria la lista de clientes.
     * Ignora mayúsculas y minúsculas.
     */
    private _filter(value: any, collection: any[]): any[] {
        if (!Array.isArray(collection)) {
            return [];
        }

        // Asegurar que value es una cadena
        const filterValue = this.normalizeText(typeof value === 'string' ? value : '');

        return collection.filter((item) => this.normalizeText(item?.name || '').includes(filterValue));
    }

    /**
     * Normaliza texto para ignorar mayúsculas, minúsculas y tildes
     */
    private normalizeText(text: string): string {
        return text
            ? text
                  .toLowerCase()
                  .normalize('NFD')
                  .replace(/[\u0300-\u036f]/g, '')
            : '';
    }
}
